import { ViewModel, View, ObservableArray } from "binder"
import { ControlPlanVersionEditorView } from "./controlPlanVersionEditor-view"
import { ServerConnection } from "src/server/serverConnection";
import { ControlPlanMeasureParameterSpecification, ControlPlanVersionViewModel, SpecificationValue } from "../controlPlan-viewModel";
import { MeasureParameter, MeasureParameterSearchableSelector } from "../../measuredParameterGroup/selectors/measureParameterSelector";
import { stringIsNullOrEmpty } from "src/common/utilities";
import { ProductSelector, ProductType } from "src/erp/inventory/rawData/product/productSelector";
import { ProviderSearchViewModel } from "src/erp/arap/rawData/providerSelector-viewModel";
import { IPopup } from "src/common/userMainWindow/windowContainer/IPopup";

export class ControlPlanVersionEditorViewModel extends ViewModel {
    GetDefaultView(): View<ViewModel> {
        return new ControlPlanVersionEditorView();
    }
    public getTypeId(): string {
        return "VM-FBFF3E15-8EAF-40D7-9C4B-4443A00189E0"
    }

    constructor(parentViewModel: ViewModel, serverUpdater: IServerUpdater, measureParameterGroupId: string, productId: string) {
        super(parentViewModel);
        this._updater = serverUpdater;
        this.measureParameterGroupId = measureParameterGroupId;
        this.productId = productId;
        this.measureParameters = ObservableArray.create<ControlPlanVersionMeasureParameter>();
        this.measureParameterSearchableSelector = new MeasureParameterSearchableSelector(this, measureParameterGroupId);

        this.productSearchableSelector = new ProductSelector(this, ProductType.FinalGood);
        this.productSearchableSelector.onSelectionChanged = selectedProduct => this.productId = selectedProduct?.id;
        this.provideSearchableSelector = new ProviderSearchViewModel(this);
        this.isDuplicateAction = false;
    }

    private _updater: IServerUpdater;
    public set isLoading(v: boolean) { this._isLoading = v; } public get isLoading(): boolean { return this._isLoading; } private _isLoading: boolean;
    public set id(v: string) { this._id = v; } public get id(): string { return this._id; } private _id: string;
    public set no(v: number) { this._no = v; } public get no(): number { return this._no; } private _no: number;
    public set standard(v: string) { this._standard = v; } public get standard(): string { return this._standard; } private _standard: string;
    public set isActive(v: boolean) { this._isActive = v; } public get isActive(): boolean { return this._isActive; } private _isActive: boolean;
    public set productId(v: string) { this._productId = v; } public get productId(): string { return this._productId; } private _productId: string;
    public set measureParameterGroupId(v: string) { this._measureParameterGroupId = v; } public get measureParameterGroupId(): string { return this._measureParameterGroupId; } private _measureParameterGroupId: string;
    public set measureParameters(v: ObservableArray<ControlPlanVersionMeasureParameter>) { this._measureParameters = v; } public get measureParameters(): ObservableArray<ControlPlanVersionMeasureParameter> { return this._measureParameters; } private _measureParameters: ObservableArray<ControlPlanVersionMeasureParameter>;
    public set measureParameterSearchableSelector(v: MeasureParameterSearchableSelector) { this._measureParameterSearchableSelector = v; } public get measureParameterSearchableSelector(): MeasureParameterSearchableSelector { return this._measureParameterSearchableSelector; } private _measureParameterSearchableSelector: MeasureParameterSearchableSelector;
    public set isDuplicateAction(v: boolean) { this._isDuplicateAction = v; } public get isDuplicateAction(): boolean { return this._isDuplicateAction; } private _isDuplicateAction: boolean;
    public set productSearchableSelector(v: ProductSelector) { this._productSearchableSelector = v; } public get productSearchableSelector(): ProductSelector { return this._productSearchableSelector; } private _productSearchableSelector: ProductSelector;
    public set provideSearchableSelector(v: ProviderSearchViewModel) { this._provideSearchableSelector = v; } public get provideSearchableSelector(): ProviderSearchViewModel { return this._provideSearchableSelector; } private _provideSearchableSelector: ProviderSearchViewModel;

    closePopup(result: any) {
        return result;
    }

    async confrim(): Promise<void> {
        try {
            this.isLoading = true;
            let validationResult = await this.validation();
            if (validationResult.length > 0) {
                await this.messageBoxConfirmWarning(validationResult.join("\n"), "طرح کنترل");
                return;
            }

            let result = await this._updater.excute(this);
            if (result == true) {
                await this.messageBoxConfirmSuccess("اطلاعات با موفقیت ثبت شد.", "طرح کنترل");
                this.closePopup(this);
            }
        } catch (ex) {
            console.log(ex);
        } finally {
            this.isLoading = false;
        }
    }

    async validation(): Promise<string[]> {
        let validation = [];
        if (this.no == null || this.no == 0) {
            validation.push("لطفا شماره بازنگری را وارد کنید.");
        }
        if (stringIsNullOrEmpty(this.standard)) {
            validation.push("لطفا نام استاندارد را وارد کنید.");
        }
        this.measureParameters.forEach(item => {
            if (item.isValuesValid == false) {
                validation.push(`مقدار Min و Max در سطر ${item.index} با هم همخوانی ندارد.`);
            } if (stringIsNullOrEmpty(item.priority.toString())) {
                validation.push(`لطفا اولویت را در سطر ${item.index} وارد کنید.`);
            }
        })
        return validation;
    }

    async editLoadById(id: string) {
        let version = await this.getVersionDetail(id);
        this.id = version.Id;
        this.no = version.No;
        this.standard = version.Standard;
        this.isActive = version.IsActice;
        this.measureParameters.update(...version.MeasureParameters.map(item => this.convertToControlPlanMeasureParameter(item)));
        this.measureParameters.sort((a, b) => a.priority-b.priority);
        this.updateMeasureParametersIndex();
    }

    async duplicateLoadById(id: string) {
        let version = await this.getVersionDetail(id);
        this.standard = version.Standard;
        this.isDuplicateAction = true;
        this.measureParameters.update(...version.MeasureParameters.map(item => this.convertToControlPlanMeasureParameter(item)));
        this.measureParameters.forEach(item => item.canRemove = true);
        this.productSearchableSelector.selectById(this.productId);
        this.updateMeasureParametersIndex();
    }

    private async getVersionDetail(id: string): Promise<any> {
        let serverResult = await ServerConnection.callServer('/Complex/ControlPlanService/GetControlPlanVersionDetail', {
            VersionId: id
        });

        return serverResult.Version;
    }

    convertToControlPlanMeasureParameter(dto: any): ControlPlanVersionMeasureParameter {
        let versionMeasureParameter: ControlPlanVersionMeasureParameter = new ControlPlanVersionMeasureParameter(this);
        versionMeasureParameter.id = dto.Id;
        versionMeasureParameter.priority = dto.Priority;
        versionMeasureParameter.measureParameterId = dto.MeasureParameterId;
        versionMeasureParameter.measureParameterName = dto.MeasureParameterName;
        versionMeasureParameter.providerId = dto.SupplierId;
        versionMeasureParameter.providerName = dto.SupplierName;
        versionMeasureParameter.minimum = dto.Minimum;
        versionMeasureParameter.maximum = dto.Maximum;
        versionMeasureParameter.accuracy = dto.Accuracy;
        versionMeasureParameter.specifications.update(...dto.Specifications.map(item => {
            let specification: ControlPlanMeasureParameterSpecification = new ControlPlanMeasureParameterSpecification(versionMeasureParameter);
            specification.id = item.Id;
            specification.title = item.Title;
            specification.selectedValues.update(...item.Values.map(v => this.convertToSpecificationValue(v, specification)));
            return specification;
        }))
        return versionMeasureParameter;
    }

    async addMeasureParameters(): Promise<void> {
        let validationResult = this.addMeasureParameterValidation();
        if (validationResult.length > 0) {
            await this.messageBoxConfirmWarning(validationResult.join("\n"), "افزودن مشخصه جدید")
            return;
        }

        let controlPlanVersionMeasureParameter: ControlPlanVersionMeasureParameter = new ControlPlanVersionMeasureParameter(this);
        controlPlanVersionMeasureParameter.measureParameterId = this.measureParameterSearchableSelector.selectedItem.id;
        controlPlanVersionMeasureParameter.measureParameterName = this.measureParameterSearchableSelector.selectedItem.name;
        controlPlanVersionMeasureParameter.providerId = this.provideSearchableSelector.selectedItem?.id;
        controlPlanVersionMeasureParameter.providerName = this.provideSearchableSelector.selectedItem?.name;
        controlPlanVersionMeasureParameter.canRemove = true;
        this.measureParameters.push(controlPlanVersionMeasureParameter);
        this.measureParameterSearchableSelector.refresh();
        this.provideSearchableSelector.refresh();
        this.updateMeasureParametersIndex();
    }

    addMeasureParameterValidation(): string[] {
        let validationList: string[] = [];
        if (this.measureParameterSearchableSelector.selectedItem == null) {
            validationList.push("لطفا مشخصه را انتخاب کنید.");
        } else if (this.isMeasureParameterExist(this.measureParameterSearchableSelector.selectedItem.id, this.provideSearchableSelector.selectedItem?.id)) {
            validationList.push("این مشخصه در لیست وجود دارد.")
        }
        return validationList;
    }

    isMeasureParameterExist(measureParameterId: string, providerId?: string): boolean {
        if (stringIsNullOrEmpty(providerId)) {
            return this.measureParameters.some(item => item.measureParameterId == measureParameterId);
        }
        return this.measureParameters.some(item => item.measureParameterId == measureParameterId && item.providerId == providerId);
    }

    removeMeasureParameters(parameter: ControlPlanVersionMeasureParameter) {
        this.measureParameters.remove(parameter);
        this.updateMeasureParametersIndex();
    }

    updateMeasureParametersIndex(): void {
        this.measureParameters.forEach((item, index) => item.index = index + 1);
    }

    async loadMeasureParameterSpecifications(measureParameter: ControlPlanVersionMeasureParameter) {
        try {
            this.isLoading = true;
            let serverResult = await ServerConnection.callServer('/Complex/ControlPlanService/GetControlPlanVersionMeasureParameterSpecifications', {
                MeasureParameterId: measureParameter.id
            });

            measureParameter.specifications.update(...serverResult.Specifications.map(item => {
                let specification: ControlPlanMeasureParameterSpecification = new ControlPlanMeasureParameterSpecification(measureParameter);
                specification.id = item.Id;
                specification.title = item.Title;
                specification.values.update(...item.Values.map(v => this.convertToSpecificationValue(v, specification)));
                specification.selectedValues.update(...item.SelectedValues.map(v => this.convertToSpecificationValue(v, specification)));
                specification.updateValueSelector();
                return specification;
            }))
        } catch (ex) {
            console.log(ex);
        } finally {
            this.isLoading = false;
        }
    }

    convertToSpecificationValue(dto: any, parent: ControlPlanMeasureParameterSpecification): SpecificationValue {
        let value: SpecificationValue = new SpecificationValue(parent);
        value.id = dto.Id;
        value.valueId = dto.ValueId;
        value.value = dto.Value;
        return value;
    }

    async saveMeasureParameterSpecificationValue(measureParameter: ControlPlanVersionMeasureParameter) {
        try {
            this.isLoading = true;
            let serverResult = await ServerConnection.callServer('/Complex/ControlPlanService/SynchroniseControlPlanVersionMeasureParameterSpecifications', {
                MeasureParameterId: measureParameter.id,
                Specifications: measureParameter.specifications.map(specification => {
                    let values = specification.selectedValues.map(value => { return { Id: value.id, ValueId: value.valueId }; });
                    return {
                        Id: specification.id,
                        Values: values
                    };
                })
            });

            return serverResult.IsSuccessful;
        } catch (ex) {
            console.log(ex);
        } finally {
            this.isLoading = false;
        }
    }
}

export class ControlPlanVersionMeasureParameter extends ViewModel implements IPopup {
    public getTypeId(): string { return 'controlPlanVersionMeasureParameter'; }
    public GetDefaultView(): View<ViewModel> { return null; }
    constructor(parent: ControlPlanVersionEditorViewModel) {
        super(parent);
        this._parent = parent;
        this.isValuesValid = true;
        this.specifications = ObservableArray.create<ControlPlanMeasureParameterSpecification>();
    }

    private _parent: ControlPlanVersionEditorViewModel;
    public set index(v: number) { this._index = v; } public get index(): number { return this._index; } private _index: number;
    public set id(v: string) { this._id = v; } public get id(): string { return this._id; } private _id: string;
    public set priority(v: number) { this._priority = v; } public get priority(): number { return this._priority;} private _priority: number;
    public set measureParameterId(v: string) { this._measureParameterId = v; } public get measureParameterId(): string { return this._measureParameterId; } private _measureParameterId: string;
    public set measureParameterName(v: string) { this._measureParameterName = v; } public get measureParameterName(): string { return this._measureParameterName; } private _measureParameterName: string;
    public set minimum(v: number) { this._minimum = v; this.requestCheckValues(); } public get minimum(): number { return this._minimum; } private _minimum: number;
    public set maximum(v: number) { this._maximum = v; this.requestCheckValues(); } public get maximum(): number { return this._maximum; } private _maximum: number;
    public set accuracy(v: number) { this._accuracy = v; } public get accuracy(): number { return this._accuracy;} private _accuracy: number;
    public set canRemove(v: boolean) { this._canRemove = v; } public get canRemove(): boolean { return this._canRemove; } private _canRemove: boolean;
    public set isValuesValid(v: boolean) { this._isValuesValid = v; } public get isValuesValid(): boolean { return this._isValuesValid; } private _isValuesValid: boolean;
    public set providerId(v: string) { this._providerId = v; } public get providerId(): string { return this._providerId; } private _providerId: string;
    public set providerName(v: string) { this._providerName = v; } public get providerName(): string { return this._providerName; } private _providerName: string;
    public set specifications(v: ObservableArray<ControlPlanMeasureParameterSpecification>) { this._specifications = v; } public get specifications(): ObservableArray<ControlPlanMeasureParameterSpecification> { return this._specifications; } private _specifications: ObservableArray<ControlPlanMeasureParameterSpecification>;
    private _checkValueTimer: NodeJS.Timeout;

    closePopup(result: boolean = false) {
        return result;
    }

    remove() {
        this._parent.removeMeasureParameters(this);
    }

    requestCheckValues(): void {
        if (this._checkValueTimer != null) {
            clearTimeout(this._checkValueTimer);
        }
        this._checkValueTimer = setTimeout(() => this.checkValues(), 1000);
    }

    checkValues(): void {
        if (this.minimum != null && this.maximum != null) {
            if (Number(this.maximum) < Number(this.minimum)) {
                this.isValuesValid = false;
                return;
            }
        }
        this.isValuesValid = true;
    }

    async showSpecifications() {
        await this.loadSpecifications();
        await this.requestShowingPopup(this, false);
    }

    async loadSpecifications() {
        await this._parent.loadMeasureParameterSpecifications(this);
    }

    async save() {
        let result = await this._parent.saveMeasureParameterSpecificationValue(this);
        if (result == true) {
            await this.messageBoxConfirmSuccess("تغییرات ویژگی ها با موفقیت ثبت شد.", "ویژگی های مشخصه");
            this.closePopup(true);
        }
    }

    back() {
        this.closePopup();
    }
}

export interface IServerUpdater {
    excute(version: ControlPlanVersionEditorViewModel): Promise<boolean>;
}

export class ControlPlanVersionInserter implements IServerUpdater {
    async excute(version: ControlPlanVersionEditorViewModel): Promise<boolean> {
        let serverResult = await ServerConnection.callServer('/Complex/ControlPlanService/CreateControlPlanVersion', {
            ProductId: version.productId,
            MeasureParameterGroupId: version.measureParameterGroupId,
            VersionNo: version.no,
            VersionStandard: version.standard,
            MeasureParameters: version.measureParameters.map(item => {
                return {
                    Priority : item.priority,
                    MeasureParameterId: item.measureParameterId,
                    SupplierId: item.providerId,
                    Minimum: item.minimum,
                    Maximum: item.maximum,
                    Accuracy: item.accuracy,
                    SpecificationValues: item.specifications.map(specification => specification.selectedValues.map(value => {return { ValueId: value.valueId }})).flat()
                }
            })
        });

        return serverResult.IsSuccessful;
    }

}

export class ControlPlanVersionUpdater implements IServerUpdater {
    async excute(version: ControlPlanVersionEditorViewModel): Promise<boolean> {
        let serverResult = await ServerConnection.callServer('/Complex/ControlPlanService/UpdateControlPlanVersion', {
            VersionId: version.id,
            VersionNo: version.no,
            VersionStandard: version.standard,
            MeasureParameters: version.measureParameters.map(item => {
                return {
                    Id: item.id,
                    Priority : item.priority,
                    MeasureParameterId: item.measureParameterId,
                    SupplierId: item.providerId,
                    Minimum: item.minimum,
                    Maximum: item.maximum,
                    Accuracy: item.accuracy
                }
            })
        });

        return serverResult.IsSuccessful;
    }

}
